import { useEffect, useState } from 'react';
import { INVITE_EXAMPLE, INVITE_LENGTH, isInviteCodeShaped, normalizeInviteCode } from '@callzone/shared';
import { api, ApiError, configureApi } from '../lib/api';
import { gateway } from '../lib/gateway';
import { useStore } from '../store';
import { CURIOSIDADES, INTERVALO_CURIOSIDADE } from '../lib/curiosidades';
import { deviceLabel, setCredentials } from '../lib/platform';
import { EyeIcon, EyeOffIcon, ArrowLeftIcon } from './icons';

/** Em dev o app roda em :5173 e o servidor em :4000; em producao sao o mesmo host. */
const DEFAULT_URL =
  window.location.port === '5173'
    ? `${window.location.protocol}//${window.location.hostname}:4000`
    : window.location.origin;

/**
 * `invite-code` pede so o token; `invite-profile` so aparece depois que o
 * codigo foi aceito, para ninguem preencher nome e senha a toa.
 */
type Step = 'server' | 'login' | 'invite-code' | 'invite-profile' | 'bootstrap';

/** Campo de senha com o olho para revelar o que foi digitado. */
function PasswordField({
  label,
  value,
  onChange,
  onEnter,
  placeholder,
  autoComplete,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  placeholder?: string;
  autoComplete?: string;
  hint?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="field">
      <label>{label}</label>
      <div className="password-field">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="password-eye"
          onClick={() => setVisible((v) => !v)}
          title={visible ? 'Ocultar senha' : 'Mostrar senha'}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

/** Coluna da marca: logo, nome e as curiosidades trocando sozinhas. */
function BrandPanel() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * CURIOSIDADES.length));

  useEffect(() => {
    const timer = setInterval(
      () => setIndex((i) => (i + 1) % CURIOSIDADES.length),
      INTERVALO_CURIOSIDADE,
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="onboarding-brand">
      <img src="/logo.png" alt="" className="onboarding-brand-logo" />
      <h2>CallZone</h2>
      <div className="onboarding-fact">
        <div>
          <div className="onboarding-fact-label">Você sabia?</div>
          {/* A chave force o React a remontar o paragrafo, disparando a animacao. */}
          <p key={index}>{CURIOSIDADES[index]}</p>
        </div>
      </div>
    </div>
  );
}

export function Onboarding() {
  const [step, setStep] = useState<Step>('server');
  const [serverUrl, setServerUrl] = useState(DEFAULT_URL);
  const [checking, setChecking] = useState(false);
  const [detecting, setDetecting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [nickname, setNickname] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [code, setCode] = useState('');
  const [guildName, setGuildName] = useState('Minha Guild');
  const [preview, setPreview] = useState<{ name: string; memberCount: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * O servidor quase sempre e o mesmo host que serviu a pagina, entao nao faz
   * sentido perguntar o endereco. Detectamos sozinhos e so mostramos aquela
   * tela se a deteccao falhar.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await api.status(DEFAULT_URL);
        if (cancelled) return;
        configureApi(DEFAULT_URL, null);
        setStep(status.initialized ? 'login' : 'bootstrap');
      } catch {
        // Deixa a tela de endereco aparecer para a pessoa digitar o servidor.
      } finally {
        if (!cancelled) setDetecting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Assim que o codigo tem a forma certa, mostra para onde ele leva.
  useEffect(() => {
    if (step !== 'invite-code' || !isInviteCodeShaped(code)) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    void api
      .previewInvite(normalizeInviteCode(code))
      .then((result) => {
        if (!cancelled) setPreview({ name: result.guild.name, memberCount: result.memberCount });
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code, step]);

  async function checkServer() {
    setChecking(true);
    setError(null);
    try {
      const url = serverUrl.trim().replace(/\/+$/, '');
      const status = await api.status(url);
      configureApi(url, null);
      setStep(status.initialized ? 'login' : 'bootstrap');
    } catch {
      setError('Nao consegui falar com esse servidor. Confira o endereco e se ele esta ligado.');
    } finally {
      setChecking(false);
    }
  }

  async function finish(token: string) {
    const url = serverUrl.trim().replace(/\/+$/, '');
    configureApi(url, token);
    // Sem "lembrar de mim" a credencial nao vai para o disco: a sessao acaba
    // quando a aba fecha. Quem resgatou convite sempre e lembrado, senao a
    // conta recem-criada se perderia.
    if (remember) setCredentials({ serverUrl: url, token });

    const store = useStore.getState();
    store.setConnection(url, token);
    store.setPhase('connecting');
    gateway.connect(token);
  }

  async function submit(action: () => Promise<string>) {
    setSubmitting(true);
    setError(null);
    try {
      await finish(await action());
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'Falha ao entrar.');
    } finally {
      setSubmitting(false);
    }
  }

  const login = () =>
    submit(async () => {
      const result = await api.login({
        nickname: nickname.trim(),
        password,
        deviceLabel: deviceLabel(),
      });
      return result.token;
    });

  const redeem = () =>
    submit(async () => {
      const result = await api.redeem({
        code: normalizeInviteCode(code),
        nickname: nickname.trim(),
        fullName: fullName.trim() || undefined,
        password: password || undefined,
        deviceLabel: deviceLabel(),
      });
      if (!result.token) throw new Error('O servidor nao devolveu um token de acesso.');
      return result.token;
    });

  const bootstrap = () =>
    submit(async () => {
      const result = await api.bootstrap({
        code: code.trim(),
        nickname: nickname.trim(),
        password,
        guildName: guildName.trim(),
        deviceLabel: deviceLabel(),
      });
      return result.token;
    });

  /** Volta do fluxo de convite para o login, limpando o que ficou pela metade. */
  function backToLogin() {
    setStep('login');
    setError(null);
    setPreview(null);
    setCode('');
    setFullName('');
  }

  return (
    <div className="onboarding">
      <BrandPanel />

      <div className="onboarding-form">
        <div className="onboarding-card">
          {detecting && (
            <>
              <h1>Um instante</h1>
              <p className="sub">Procurando o servidor...</p>
            </>
          )}

          {!detecting && step === 'server' && (
            <>
              <h1>Conectar a um servidor</h1>
              <p className="sub">
                O CallZone nao tem cadastro aberto: so se entra por convite. Comece apontando para o
                servidor da sua guild.
              </p>
              {error && <div className="error-box">{error}</div>}
              <div className="field">
                <label>Endereco do servidor</label>
                <input
                  value={serverUrl}
                  onChange={(e) => setServerUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void checkServer()}
                  placeholder="http://localhost:4000"
                  autoFocus
                />
              </div>
              <button className="btn full" onClick={() => void checkServer()} disabled={checking}>
                {checking ? 'Procurando...' : 'Continuar'}
              </button>
            </>
          )}

          {!detecting && step === 'login' && (
            <>
              <h1>Entrar</h1>
              <p className="sub">Faça login para acessar a plataforma</p>

              {error && <div className="error-box">{error}</div>}

              <div className="field">
                <label>Nome de usuário</label>
                <input
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Digite seu usuário"
                  maxLength={32}
                  autoFocus
                  autoComplete="username"
                />
              </div>

              <PasswordField
                label="Senha"
                value={password}
                onChange={setPassword}
                onEnter={() => void login()}
                placeholder="Digite sua senha"
                autoComplete="current-password"
              />

              <div
                className="switch-row"
                role="switch"
                aria-checked={remember}
                tabIndex={0}
                onClick={() => setRemember((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setRemember((v) => !v);
                  }
                }}
              >
                <span className="switch-label">Lembrar de mim</span>
                <span className={`switch ${remember ? 'on' : ''}`} />
              </div>

              <button
                className="btn full"
                onClick={() => void login()}
                disabled={submitting || nickname.trim().length < 2 || password.length < 1}
              >
                {submitting ? 'Entrando...' : 'Entrar'}
              </button>

              <div className="divider-or">ou</div>

              <button
                className="btn outline full"
                onClick={() => {
                  setStep('invite-code');
                  setError(null);
                }}
              >
                Tenho um convite
              </button>
            </>
          )}

          {!detecting && step === 'invite-code' && (
            <>
              <div className="back-row">
                <button className="back-btn" onClick={backToLogin}>
                  <ArrowLeftIcon />
                  Voltar
                </button>
              </div>

              <h1>Tenho um convite</h1>
              <p className="sub">Cole o código que um administrador gerou para você.</p>

              {error && <div className="error-box">{error}</div>}

              <div className="field">
                <label>Código do convite</label>
                <input
                  className="mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && preview) setStep('invite-profile');
                  }}
                  placeholder={INVITE_EXAMPLE}
                  maxLength={INVITE_LENGTH + 6}
                  autoFocus
                />
                {preview ? (
                  <div className="hint" style={{ color: 'var(--green)' }}>
                    Convite válido para {preview.name} · {preview.memberCount}{' '}
                    {preview.memberCount === 1 ? 'membro' : 'membros'}
                  </div>
                ) : (
                  <div className="hint">
                    Começa com LJ e termina com MM, {INVITE_LENGTH} caracteres ao todo.
                  </div>
                )}
              </div>

              <button
                className="btn full"
                onClick={() => setStep('invite-profile')}
                disabled={!preview}
              >
                Continuar
              </button>
            </>
          )}

          {!detecting && step === 'invite-profile' && (
            <>
              <div className="back-row">
                <button
                  className="back-btn"
                  onClick={() => {
                    setStep('invite-code');
                    setError(null);
                  }}
                >
                  <ArrowLeftIcon />
                  Voltar
                </button>
              </div>

              <h1>Criar sua conta</h1>
              <p className="sub">
                {preview
                  ? `Você está entrando em ${preview.name}.`
                  : 'Preencha seus dados para entrar.'}
              </p>

              {error && <div className="error-box">{error}</div>}

              <div className="field">
                <label>Nome</label>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Seu nome completo"
                  maxLength={64}
                  autoFocus
                  autoComplete="name"
                />
                <div className="hint">Aparece no seu perfil. Pode deixar em branco.</div>
              </div>

              <div className="field">
                <label>Apelido</label>
                <input
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Como as pessoas vão te ver"
                  maxLength={32}
                  autoComplete="username"
                />
                <div className="hint">É também o seu nome de usuário para entrar depois.</div>
              </div>

              <PasswordField
                label="Senha"
                value={password}
                onChange={setPassword}
                onEnter={() => void redeem()}
                placeholder="Crie uma senha"
                autoComplete="new-password"
                hint="Mínimo de 8 caracteres. Sem senha, o acesso fica preso a este navegador."
              />

              <button
                className="btn full"
                onClick={() => void redeem()}
                disabled={
                  submitting ||
                  nickname.trim().length < 2 ||
                  (password.length > 0 && password.length < 8)
                }
              >
                {submitting ? 'Entrando...' : 'Entrar na guild'}
              </button>
            </>
          )}

          {!detecting && step === 'bootstrap' && (
            <>
              <h1>Servidor novo</h1>
              <p className="sub">
                Ninguem foi cadastrado ainda. Use o codigo mestre (BOOTSTRAP_CODE do servidor) para
                criar o primeiro administrador e a primeira guild.
              </p>
              {error && <div className="error-box">{error}</div>}

              <div className="field">
                <label>Codigo mestre</label>
                <input
                  className="mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="PRIMEIRO-ADMIN"
                  autoFocus
                />
              </div>
              <div className="field">
                <label>Seu apelido</label>
                <input
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  maxLength={32}
                  autoComplete="username"
                />
              </div>
              <PasswordField
                label="Senha"
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                hint="Minimo de 8 caracteres."
              />
              <div className="field">
                <label>Nome da guild</label>
                <input
                  value={guildName}
                  onChange={(e) => setGuildName(e.target.value)}
                  maxLength={64}
                  onKeyDown={(e) => e.key === 'Enter' && void bootstrap()}
                />
              </div>

              <button
                className="btn full"
                onClick={() => void bootstrap()}
                disabled={
                  submitting || !code.trim() || nickname.trim().length < 2 || password.length < 8
                }
              >
                {submitting ? 'Criando...' : 'Criar guild e entrar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
